import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import HelpTip from '@/components/HelpTip';
import { Gauge, Plus, Trash2, ArrowUp, ArrowDown, Save, Target } from 'lucide-react';

const MONTHS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

export default function CalificacionesForecastPage() {
  const [ratings, setRatings] = useState([]);
  const [plan, setPlan] = useState({ year: new Date().getFullYear(), months: [] });
  const [loading, setLoading] = useState(true);
  const [savingR, setSavingR] = useState(false);
  const [savingF, setSavingF] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/cem-ratings'),
      api.get('/cem-ratings/forecast-plan'),
    ]).then(([r, f]) => {
      setRatings(r.data?.ratings || []);
      setPlan(f.data || { year: new Date().getFullYear(), months: [] });
    }).catch(() => toast.error('No se pudieron cargar las calificaciones')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= ratings.length) return;
    const next = [...ratings];
    [next[idx], next[j]] = [next[j], next[idx]];
    setRatings(next.map((x, i) => ({ ...x, order: i + 1 })));
  };

  const saveRatings = async () => {
    setSavingR(true);
    try {
      const r = await api.put('/cem-ratings', { ratings });
      setRatings(r.data.ratings);
      toast.success('Calificaciones guardadas. Ya se ven en el panel de Leads.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally { setSavingR(false); }
  };

  const savePlan = async () => {
    setSavingF(true);
    try {
      const r = await api.put('/cem-ratings/forecast-plan', plan);
      setPlan(r.data);
      toast.success('Forecast del año guardado. El dashboard de ventas lo usa como meta.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar el forecast');
    } finally { setSavingF(false); }
  };

  const setMonth = (month, target) => {
    setPlan(p => ({
      ...p,
      months: (p.months || []).map(m => m.month === month ? { ...m, target } : m),
    }));
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="page-calificaciones-forecast">
      <div>
        <h1 className="font-heading text-2xl font-bold text-white flex items-center gap-2">
          <Gauge className="w-6 h-6 text-amber-400" /> Calificaciones Forecast
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          CEM, no CRM. Aquí el vendedor dice <strong className="text-white">qué tan interesado está el lead</strong>.
          El pipeline (Prospecto → Cierre) vive en <Link to="/opportunities" className="text-primary underline">Oportunidades</Link>.
        </p>
      </div>

      <Card className="bg-card/50 border-white/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2">
            Calificaciones de interés
            <HelpTip title="Qué es esto" content="Orden de menor a mayor interés. El seller score (1 a 10) es la nota que pone el vendedor; compite con el Score IA en el dashboard de ventas." />
          </CardTitle>
          <CardDescription className="text-xs">
            Por defecto: No Localizable → Localizable sin Interés → Interesado → Muy interesado → Muy interesado no perfilado.
            El administrador puede renombrarlas, reordenarlas y cambiar el puntaje.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading && <p className="text-xs text-muted-foreground">Cargando…</p>}
          {ratings.map((r, i) => (
            <div key={r.id || i} className="grid grid-cols-12 gap-2 items-center p-2 rounded-lg bg-black/20 border border-white/5" data-testid={`rating-row-${i}`}>
              <div className="col-span-1 flex flex-col gap-1">
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp className="w-3 h-3" /></Button>
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => move(i, 1)} disabled={i === ratings.length - 1}><ArrowDown className="w-3 h-3" /></Button>
              </div>
              <div className="col-span-1">
                <input type="color" value={r.color || '#64748B'} onChange={e => setRatings(rs => rs.map((x, k) => k === i ? { ...x, color: e.target.value } : x))} className="w-8 h-8 rounded cursor-pointer border-0" />
              </div>
              <div className="col-span-6">
                <Input value={r.label} onChange={e => setRatings(rs => rs.map((x, k) => k === i ? { ...x, label: e.target.value } : x))} className="bg-black/20 border-white/10 h-8 text-sm" />
              </div>
              <div className="col-span-3">
                <Label className="text-[9px] text-muted-foreground">Seller score 1–10</Label>
                <Input type="number" min={1} max={10} value={r.seller_score} onChange={e => setRatings(rs => rs.map((x, k) => k === i ? { ...x, seller_score: Number(e.target.value) } : x))} className="bg-black/20 border-white/10 h-8 text-sm" />
              </div>
              <div className="col-span-1 flex justify-end">
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive" onClick={() => setRatings(rs => rs.filter((_, k) => k !== i))} disabled={ratings.length <= 2}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
          <div className="flex gap-2">
            <Button variant="outline" className="border-white/10 text-xs h-8" onClick={() => setRatings(rs => [...rs, { id: `calif_${Date.now()}`, label: 'Nueva calificación', order: rs.length + 1, seller_score: 5, color: '#64748B' }])} disabled={ratings.length >= 12}>
              <Plus className="w-3 h-3 mr-1" /> Agregar
            </Button>
            <Button onClick={saveRatings} disabled={savingR} className="text-xs h-8" data-testid="save-ratings-btn">
              <Save className="w-3 h-3 mr-1" /> {savingR ? 'Guardando…' : 'Guardar calificaciones'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card/50 border-white/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Target className="w-4 h-4 text-violet-400" /> Forecast {plan.year}
            <HelpTip title="Para qué sirve" content="La meta de venta del mes. El gráfico Forecast vs Venta real del dashboard compara esta cifra con lo ganado en Oportunidades. Sin esto, el gráfico no tiene contra qué medirse." />
          </CardTitle>
          <CardDescription className="text-xs">Meta de venta en MXN por mes. El dashboard de ventas la usa como “Forecast”.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 mb-3">
            <Label className="text-xs">Año</Label>
            <Input type="number" className="w-24 h-8 bg-black/20 border-white/10" value={plan.year} onChange={e => {
              const y = Number(e.target.value);
              setPlan(p => ({ ...p, year: y }));
              api.get('/cem-ratings/forecast-plan', { params: { year: y } }).then(r => setPlan(r.data)).catch(() => {});
            }} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {(plan.months || []).map(m => (
              <div key={m.month} className="p-2 rounded-lg bg-black/20 border border-white/5">
                <Label className="text-[10px] text-muted-foreground">{MONTHS[m.month - 1]}</Label>
                <Input type="number" min={0} value={m.target || ''} onChange={e => setMonth(m.month, Number(e.target.value || 0))} className="h-8 bg-black/20 border-white/10 text-sm" />
              </div>
            ))}
          </div>
          <Button onClick={savePlan} disabled={savingF} className="mt-3 text-xs h-8" data-testid="save-forecast-btn">
            <Save className="w-3 h-3 mr-1" /> {savingF ? 'Guardando…' : 'Guardar forecast'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
